import { Injectable, signal } from "@angular/core";

export type SystemBannerSeverity = "info" | "warning" | "error" | "success";

export interface SystemBannerAction {
  label: string;
  callback: () => void;
}

export interface SystemBannerConfig {
  id: string;
  severity: SystemBannerSeverity;
  message: string;
  dismissible?: boolean;
  action?: SystemBannerAction;
  /** Called when the user dismisses this banner (e.g. to persist it). */
  onDismiss?: () => void;
}

@Injectable({ providedIn: "root" })
export class SystemBannerService {
  private _banners = signal<SystemBannerConfig[]>([]);
  private dismissedIds = new Set<string>();

  readonly banners = this._banners.asReadonly();

  show(config: SystemBannerConfig): void {
    if (this.dismissedIds.has(config.id)) return;
    const current = this._banners();
    if (current.some((b) => b.id === config.id)) return;
    this._banners.set([
      ...current,
      { dismissible: true, ...config },
    ]);
  }

  dismiss(id: string): void {
    this.dismissedIds.add(id);
    const dismissed = this._banners().find((b) => b.id === id);
    this._banners.set(this._banners().filter((b) => b.id !== id));
    dismissed?.onDismiss?.();
  }

  clear(id: string): void {
    this._banners.set(this._banners().filter((b) => b.id !== id));
  }

  info(id: string, message: string): void {
    this.show({ id, severity: "info", message });
  }

  warn(id: string, message: string): void {
    this.show({ id, severity: "warning", message });
  }

  error(id: string, message: string): void {
    this.show({ id, severity: "error", message });
  }

  success(id: string, message: string): void {
    this.show({ id, severity: "success", message });
  }
}
